import * as Types from '@aeppic/types'
import { Condition, ExplicitOperator, FieldMatchNode, isFieldMatchNode, MatchExpression, MatchExpressionMultiMatch, QueryParameters } from './types'
import { Index, IndexMap } from '../indexing/document-index'
import { SingleQueryStats } from 'model/indexing/query-stats.js'

import BTreeModule from 'sorted-btree'
const BTree: typeof BTreeModule = (BTreeModule as any).default ?? BTreeModule

type DocumentMatchIteratorWeight = {
  type: Symbol
  /**
   * The cost is an approximation of the number of documents that might need to be enumerated
   * and thus require testing 
   */
  cost: number
  isSortable: boolean
}

export const IterateNoDocuments = Symbol('IterateOverNoDocuments')
export const IterateAllDocuments = Symbol('IterateOverAllDocuments')
export const IterateOverSubIterators = Symbol('IterateOverSubIterators')
export const IterateOverId = Symbol('IterateOverId')
export const IterateOverIndex = Symbol('IterateOverIndex')

type DocumentMatchIteratorAllDocuments = DocumentMatchIteratorWeight

type DocumentMatchIteratorUsingId = DocumentMatchIteratorWeight & {
  id: string
}

type DocumentMatchIteratorUsingIndex = DocumentMatchIteratorWeight & {
  key: string
  index: Index
  set: Set<Types.DocumentId> | BTreeModule<Types.DocumentId>
}

type DocumentMatchIteratorOverNoDocuments = DocumentMatchIteratorUsingIndex

type DocumentMatchIteratorJoined= DocumentMatchIteratorWeight & {
  operator: 'intersect'|'union'
  iterators: (DocumentMatchIteratorUsingIndex|DocumentMatchIteratorJoined|DocumentMatchIteratorUsingId)[]
}

function describeIterator(iterator: any): string {
  if (iterator.type === IterateOverId) {
    return `IterateOverId(${iterator.id})`
  } else if (iterator.type === IterateOverIndex) {
    return `IterateOverIndex(${iterator.index.fieldPath} = ${iterator.key})`
  } else if (iterator.type === IterateAllDocuments) {
    return 'IterateAllDocuments'
  } else if (iterator.type === IterateNoDocuments) {
    return 'IterateNoDocuments'
  } else if (iterator.type === KMergedUnion) {
    return `KMergedUnion(${iterator.iterators.length} iterators)`
  } else if (iterator.type === KMergedIntersection) {
    return `KMergedIntersection(${iterator.iterators.length} iterators)`
  } else if (iterator.type === IterateOverSubIterators) {
    return `IterateOverSubIterators(${iterator.operator} [ ${iterator.iterators.map(i => describeIterator(i)).join(', ')} ]) `
  }

  return 'Unknown iterator'
}

// New K-Merge Iterator Symbols
export const KMergedUnion = Symbol('KMergedUnion')
export const KMergedIntersection = Symbol('KMergedIntersection')

// Interfaces for K-Merge Iterators
export interface KMergedUnionIterator extends DocumentMatchIteratorWeight {
  type: typeof KMergedUnion
  iterators: DocumentMatchIterator[] // Sub-iterators that can provide sorted DocumentIds
}

export interface KMergedIntersectionIterator extends DocumentMatchIteratorWeight {
  type: typeof KMergedIntersection
  iterators: DocumentMatchIterator[] // Sub-iterators that can provide sorted DocumentIds
}

export type DocumentMatchIterator = DocumentMatchIteratorUsingId
  | DocumentMatchIteratorWeight
  | DocumentMatchIteratorUsingIndex
  | DocumentMatchIteratorJoined
  | KMergedUnionIterator
  | KMergedIntersectionIterator

/**
 * A generic MinHeap implementation.
 * The heap stores elements of type T and orders them based on a provided comparison function.
 */
class MinHeap<T> {
  private heap: T[] = []
  private compareFn: (a: T, b: T) => number

  /**
   * Creates a new MinHeap.
   * @param compareFn A function that compares two elements (a, b) of type T. 
   *                  It should return a negative number if a < b, zero if a === b, and a positive number if a > b.
   */
  constructor(compareFn: (a: T, b: T) => number) {
    this.compareFn = compareFn
  }

  /**
   * Adds an item to the heap.
   * @param item The item to add.
   */
  public push(item: T): void {
    this.heap.push(item)
    this.siftUp()
  }

  /**
   * Removes and returns the smallest item from the heap.
   * @returns The smallest item, or undefined if the heap is empty.
   */
  public pop(): T | undefined {
    if (this.isEmpty()) return undefined
    const item = this.heap[0]
    const last = this.heap.pop()
    if (last !== undefined && !this.isEmpty()) {
      this.heap[0] = last
      this.siftDown()
    }
    return item
  }

  /**
   * Returns the smallest item from the heap without removing it.
   * @returns The smallest item, or undefined if the heap is empty.
   */
  public peek(): T | undefined {
    return this.heap[0]
  }

  /**
   * Checks if the heap is empty.
   * @returns True if the heap is empty, false otherwise.
   */
  public isEmpty(): boolean {
    return this.heap.length === 0
  }

  /**
   * Returns the number of items in the heap.
   * @returns The size of the heap.
   */
  public size(): number {
    return this.heap.length
  }

  private siftUp(): void {
    let nodeIndex = this.heap.length - 1
    while (nodeIndex > 0) {
      const parentIndex = Math.floor((nodeIndex - 1) / 2)
      if (this.compareFn(this.heap[nodeIndex], this.heap[parentIndex]) < 0) {
        [this.heap[nodeIndex], this.heap[parentIndex]] = [this.heap[parentIndex], this.heap[nodeIndex]]
        nodeIndex = parentIndex
      } else {
        break
      }
    }
  }

  private siftDown(): void {
    let nodeIndex = 0
    while (true) {
      const leftChildIndex = 2 * nodeIndex + 1
      const rightChildIndex = 2 * nodeIndex + 2
      let smallestChildIndex = nodeIndex

      if (leftChildIndex < this.heap.length && this.compareFn(this.heap[leftChildIndex], this.heap[smallestChildIndex]) < 0) {
        smallestChildIndex = leftChildIndex
      }
      if (rightChildIndex < this.heap.length && this.compareFn(this.heap[rightChildIndex], this.heap[smallestChildIndex]) < 0) {
        smallestChildIndex = rightChildIndex
      }

      if (smallestChildIndex !== nodeIndex) {
        [this.heap[nodeIndex], this.heap[smallestChildIndex]] = [this.heap[smallestChildIndex], this.heap[nodeIndex]]
        nodeIndex = smallestChildIndex
      } else {
        break
      }
    }
  }
}


/**
 * Optimizes intersection iterators using cost-based analysis at graph creation time.
 * This function applies optimization rules to sibling iterators based on their relative costs.
 * 
 * IMPORTANT: This optimization is only safe for intersection queries where post-filtering
 * will ensure correctness. It should NOT be used for union queries.
 */
function optimizeSiblingIterators(
  iterators: DocumentMatchIterator[],
  options?: OptimizationOptions,
  stats?: SingleQueryStats,
  operatorType: 'intersect' | 'union' = 'intersect'
): DocumentMatchIterator[] {
  if (!options?.enableCostBasedShortcut || iterators.length <= 1) {
    return iterators;
  }

  // Never optimize union queries - they need all iterators for correctness
  if (operatorType === 'union') {
    return iterators;
  }

  // Sort iterators by cost (smallest first)
  const sortedIterators = [...iterators].sort((a, b) => a.cost - b.cost);
  const selectivityThreshold = options.selectivityThreshold ?? 100;
  const costThresholdMultiplier = options.costThresholdMultiplier ?? 10;
  
  // Apply optimization rules as described:
  // 1. If any iterator is empty, replace all with empty
  if (sortedIterators.some(it => it.type === IterateNoDocuments || it.cost === 0)) {
    stats?.mark('sibling-optimization:empty-iterator-found');
    return [{ type: IterateNoDocuments, cost: 0, isSortable: true }];
  }
  
  // 2. For intersection: Only apply aggressive optimization if we can guarantee correctness
  // Check first iterator against selectivity threshold - but keep more conservative
  const firstIterator = sortedIterators[0];

  // If the first iterator is already very selective, we can optimize 
  if (firstIterator.cost < selectivityThreshold) {
    stats?.mark('sibling-optimization:selective-first-iterator', {
      firstCost: firstIterator.cost,
      selectivityThreshold,
      totalIterators: sortedIterators.length
    });

    // Record in the format expected by tests
    stats?.mark('graph-optimization:intersection-iterators-removed', {
      originalCount: sortedIterators.length,
      optimizedCount: 1,
      removedCount: sortedIterators.length - 1,
      mostSelectiveCost: firstIterator.cost,
      selectivityThreshold,
      costThresholdMultiplier,
      mergeCostEstimate: sortedIterators.reduce((sum, it) => sum + it.cost, 0),
      triggers: {
        isHighlySelective: true,
        isBelowCostThreshold: false,
        primaryTrigger: 'highly-selective'
      }
    });

    return [firstIterator];
  }

  // Less aggressive pairwise comparison
  let optimizedIterators = [...sortedIterators];

  for (let i = 0; i < sortedIterators.length - 1; i++) {
    const currentIterator = sortedIterators[i];
    const nextIterator = sortedIterators[i + 1];
        
    // Much more conservative threshold - only remove if the cost difference is extreme
    const costRatio = nextIterator.cost / currentIterator.cost; // E.g  1000 / 100 => 10.0 
    const nextIteratorIsMuchMoreExpensive = costRatio > costThresholdMultiplier; // E.g 10.0 > 10 => true

    if (nextIteratorIsMuchMoreExpensive) {
      stats?.mark('sibling-optimization:cost-threshold-hit', {
        currentCost: currentIterator.cost,
        nextCost: nextIterator.cost,
        costThresholdMultiplier,
        ratio: costRatio,
        removedCount: sortedIterators.length - (i + 1)
      });
      
      // Record in the format expected by tests
      stats?.mark('graph-optimization:intersection-iterators-removed', {
        originalCount: sortedIterators.length,
        optimizedCount: i + 1,
        removedCount: sortedIterators.length - (i + 1),
        mostSelectiveCost: sortedIterators[0].cost,
        selectivityThreshold,
        costThresholdMultiplier,
        mergeCostEstimate: sortedIterators.reduce((sum, it) => sum + it.cost, 0),
        triggers: {
          isHighlySelective: false,
          isBelowCostThreshold: true,
          primaryTrigger: 'below-cost-threshold'
        }
      });
      
      // Remove all iterators from position i+1 onwards
      optimizedIterators = sortedIterators.slice(0, i + 1);
      break;
    }
  }
  
  return optimizedIterators;
}

/**
 * Represents an element stored in the MinHeap during a K-Way Union operation.
 * It's a tuple containing:
 *  - `documentId`: The current DocumentId from the sub-iterator.
 *  - `iterator`: The sub-iterator (generator) itself.
 *  - `iteratorIndex`: The original index of the sub-iterator (for debugging or tracking purposes).
 */
type KWayUnionHeapElement = [
  documentId: Types.DocumentId,
  iterator: Generator<Types.DocumentId, void, unknown>,
  iteratorIndex: number
];

/**
 * Creates a generator that yields sorted DocumentIds from a given DocumentMatchIterator.
 * This function is crucial for providing sorted streams to k-way merge algorithms.
 * It handles various iterator types, including those based on B-Trees, Sets, and recursively
 * calls k-merge enumeration functions if it encounters KMergedUnion or KMergedIntersection iterators.
 *
 * @param iterator The DocumentMatchIterator to process.
 * @param allDocuments Optional map of all documents, used by IterateAllDocuments to get a sorted list of all IDs.
 * @yields {Types.DocumentId} DocumentIds in sorted order.
 * @throws Error if an unsupported iterator type is encountered or if an old IterateOverSubIterators is found.
 */
export function* getDocumentIdGenerator(iterator: DocumentMatchIterator, allDocuments?: Map<string, Types.Document>): Generator<Types.DocumentId, void, unknown> {
  if (isIteratorUsingIndexOfDocuments(iterator)) {
    if (iterator.index.isBTree) {
      // For BTree used as a Set, document IDs are stored as keys (with null values)
      for (const docId of iterator.set.keys()) {
        yield docId
      }
    } else if (iterator.set instanceof Map) {
      // For Map, use values() for document IDs
      for (const docId of iterator.set.values()) {
        yield docId
      }
    } else if (iterator.set instanceof Set) {
      // For standard Set, sort DocumentIds. This can be costly for large sets.
      const sortedIds = [...iterator.set.values()].sort()
      for (const docId of sortedIds) {
        yield docId
      }
    } else {
      console.warn('Unhandled set type in getDocumentIdGenerator for IterateOverIndex, assuming iterable and sorted:', iterator.set)
      for (const docId of iterator.set.values()) {
        yield docId
      }
    }
  } else if (isIteratorUsingId(iterator)) {
    yield iterator.id
  } else if (iterator.type === IterateNoDocuments) {
    // Yield nothing
  } else if (iterator.type === KMergedUnion) {
    yield* enumerateKMergedUnion(iterator as KMergedUnionIterator, allDocuments)
  } else if (iterator.type === KMergedIntersection) {
    yield* enumerateKMergedIntersection(iterator as KMergedIntersectionIterator, allDocuments)
  } else if (iterator.type === IterateAllDocuments && allDocuments) {
    const sortedIds = [...allDocuments.keys()].sort()
    for (const id of sortedIds) {
      yield id
    }
  } else if (isIteratorUsingJoinsOfIterators(iterator)) {
    // Handle old IterateOverSubIterators by recursively processing sub-iterators
    if (iterator.operator === 'union') {
      // For union: yield all documents from all sub-iterators (with deduplication handled by caller)
      for (const subIterator of iterator.iterators) {
        yield* getDocumentIdGenerator(subIterator, allDocuments)
      }
    } else if (iterator.operator === 'intersect') {
      // For intersection: this is more complex and expensive without sorting
      // Fall back to collecting all IDs and computing intersection manually
      const subIteratorIds: Set<Types.DocumentId>[] = []
      for (const subIterator of iterator.iterators) {
        const ids = new Set<Types.DocumentId>()
        for (const docId of getDocumentIdGenerator(subIterator, allDocuments)) {
          ids.add(docId)
        }
        subIteratorIds.push(ids)
      }
      
      if (subIteratorIds.length === 0) return
      
      // Find intersection of all sets
      let intersection = subIteratorIds[0]
      for (let i = 1; i < subIteratorIds.length; i++) {
        const newIntersection = new Set<Types.DocumentId>()
        for (const docId of intersection) {
          if (subIteratorIds[i].has(docId)) {
            newIntersection.add(docId)
          }
        }
        intersection = newIntersection
      }
      
      // Yield intersection in sorted order
      const sortedIds = [...intersection].sort()
      for (const docId of sortedIds) {
        yield docId
      }
    } else {
      throw new Error(`Unsupported operator for old IterateOverSubIterators: ${iterator.operator}`)
    }
  } else {
    // Consider other iterator types or throw an error for unsupported ones
    // For IterateAllDocuments without allDocuments, it's problematic.
    throw new Error(`Unsupported iterator type for getDocumentIdGenerator: ${String(iterator.type)}`)
  }
}

/**
 * Performs a K-Way Merge Union of DocumentIds from multiple sub-iterators.
 * It expects that `getDocumentIdGenerator` can provide sorted DocumentIds from each sub-iterator.
 * Uses a MinHeap to efficiently find the next smallest unique DocumentId across all sub-iterators.
 *
 * @param iterator The KMergedUnionIterator containing the sub-iterators to merge.
 * @param allDocuments Optional map of all documents, passed down to `getDocumentIdGenerator`.
 * @yields {Types.DocumentId} Unique DocumentIds in sorted order from the union of sub-iterators.
 */
export function* enumerateKMergedUnion(iterator: KMergedUnionIterator, allDocuments?: Map<string, Types.Document>, stats?: SingleQueryStats): Generator<Types.DocumentId, void, unknown> {
  if (iterator.iterators.length === 0) return
  if (iterator.iterators.length === 1) {
    yield* getDocumentIdGenerator(iterator.iterators[0], allDocuments)
    return
  }

  const heap = new MinHeap<KWayUnionHeapElement>((a, b) => {
    if (a[0] < b[0]) return -1;
    if (a[0] > b[0]) return 1;
    return 0;
  });

  // Initialize the heap with the first element from each sub-iterator
  for (let i = 0; i < iterator.iterators.length; i++) {
    const subIteratorGenerator = getDocumentIdGenerator(iterator.iterators[i], allDocuments);
    const firstElement = subIteratorGenerator.next();
    if (!firstElement.done) {
      heap.push([firstElement.value as string, subIteratorGenerator, i]);
    }
  }

  let lastYieldedDocumentId: Types.DocumentId | null = null;
  let heapOperations = 0;
  let documentsYielded = 0;

  while (!heap.isEmpty()) {
    heapOperations++;
    const heapTopElement = heap.pop()!; // [documentId, subIteratorGenerator, originalIteratorIndex]
    const currentDocumentId = heapTopElement[0];
    const currentSubGenerator = heapTopElement[1];
    const originalIteratorIndex = heapTopElement[2];

    if (currentDocumentId !== lastYieldedDocumentId) {
      documentsYielded++;
      yield currentDocumentId;
      lastYieldedDocumentId = currentDocumentId;
    }

    // Get the next element from the sub-iterator that provided the top element
    const nextElement = currentSubGenerator.next();
    if (!nextElement.done) {
      heapOperations++;
      heap.push([nextElement.value as string, currentSubGenerator, originalIteratorIndex]);
    }
  }

  stats?.mark('k-merge-union-stats', {
    heapOperations,
    documentsYielded,
    opsPerDocument: documentsYielded > 0 ? heapOperations / documentsYielded : 0,
    iteratorCount: iterator.iterators.length
  });
}

/**
 * Performs a K-Way Merge Intersection of DocumentIds from multiple sub-iterators.
 * It expects that `getDocumentIdGenerator` can provide sorted DocumentIds from each sub-iterator.
 * Uses a multi-pointer strategy: advances iterators whose current DocumentId is less than the
 * maximum current DocumentId across all iterators until all point to the same ID (which is then yielded),
 * or an iterator is exhausted.
 *
 * @param iterator The KMergedIntersectionIterator containing the sub-iterators to intersect.
 * @param allDocuments Optional map of all documents, passed down to `getDocumentIdGenerator`.
 * @yields {Types.DocumentId} DocumentIds in sorted order that are common to all sub-iterators.
 */
export function* enumerateKMergedIntersection(iterator: KMergedIntersectionIterator, allDocuments?: Map<string, Types.Document>, stats?: SingleQueryStats): Generator<Types.DocumentId, void, unknown> {
  if (iterator.iterators.length === 0) return
  // Optimization: if any sub-iterator is IterateNoDocuments, intersection is empty
  if (iterator.iterators.some(it => it.type === IterateNoDocuments)) return

  if (iterator.iterators.length === 1) {
    yield* getDocumentIdGenerator(iterator.iterators[0], allDocuments)
    return
  }

  const subIteratorGenerators: Generator<Types.DocumentId, void, unknown>[] = [];
  const currentDocumentIdsFromIterators: (Types.DocumentId | null)[] = []; // Stores the current DocumentId from each sub-iterator

  // Initialize generators and populate currentDocumentIdsFromIterators with the first ID from each
  for (const subIterator of iterator.iterators) {
    const docIdGenerator = getDocumentIdGenerator(subIterator, allDocuments);
    subIteratorGenerators.push(docIdGenerator);
    const firstElement = docIdGenerator.next();
    if (firstElement.done) {
      // If any sub-iterator is empty, the intersection is empty.
      return;
    }
    currentDocumentIdsFromIterators.push(firstElement.value as string);
  }

  let iteratorAdvances = 0;
  let comparisonOperations = 0;
  let documentsYielded = 0;

  mainLoop: while (true) {
    let allDocumentIdsAreIdentical = true;
    let maximumDocumentId = currentDocumentIdsFromIterators[0]!;

    // Check if all current document IDs are the same and find the maximum ID
    for (let i = 0; i < currentDocumentIdsFromIterators.length; i++) {
      const currentId = currentDocumentIdsFromIterators[i];
      if (currentId === null) return; // Should ideally not happen if logic is correct

      comparisonOperations++;
      if (i > 0 && currentId !== currentDocumentIdsFromIterators[0]) {
        allDocumentIdsAreIdentical = false;
      }
      if (currentId > maximumDocumentId) {
        maximumDocumentId = currentId;
      }
    }

    if (allDocumentIdsAreIdentical) {
      // All iterators point to the same DocumentId, so yield it.
      documentsYielded++;
      yield currentDocumentIdsFromIterators[0]!;

      // Advance all iterators to their next DocumentId.
      for (let i = 0; i < subIteratorGenerators.length; i++) {
        iteratorAdvances++;
        const nextElement = subIteratorGenerators[i].next();
        if (nextElement.done) {
          // If any iterator is exhausted, the intersection is complete.
          stats?.mark('k-merge-intersection-stats', {
            iteratorAdvances,
            comparisonOperations,
            documentsYielded,
            advancesPerDocument: documentsYielded > 0 ? iteratorAdvances / documentsYielded : 0,
            comparisonsPerDocument: documentsYielded > 0 ? comparisonOperations / documentsYielded : 0,
            iteratorCount: iterator.iterators.length
          });
          return;
        }
        currentDocumentIdsFromIterators[i] = nextElement.value as string;
      }
    } else {
      // Not all DocumentIds are the same. Advance iterators whose current DocumentId is less than maximumDocumentId.
      for (let i = 0; i < subIteratorGenerators.length; i++) {
        while (currentDocumentIdsFromIterators[i]! < maximumDocumentId) {
          iteratorAdvances++;
          const nextElement = subIteratorGenerators[i].next();
          if (nextElement.done) {
            // If any iterator is exhausted while trying to catch up, the intersection is complete.
            stats?.mark('k-merge-intersection-stats', {
              iteratorAdvances,
              comparisonOperations,
              documentsYielded,
              advancesPerDocument: documentsYielded > 0 ? iteratorAdvances / documentsYielded : 0,
              comparisonsPerDocument: documentsYielded > 0 ? comparisonOperations / documentsYielded : 0,
              iteratorCount: iterator.iterators.length
            });
            return;
          }
          currentDocumentIdsFromIterators[i] = nextElement.value as string;
        }
        // If an iterator, after advancing, has a DocumentId greater than the current maximumDocumentId,
        // it means the previous maximumDocumentId was not a common value. 
        // The loop will continue, and a new maximumDocumentId will be determined in the next iteration.
      }
    }
  }
}


export function isEmptyIterator(iterator: DocumentMatchIteratorWeight): boolean {
  return iterator.cost === 0 || iterator.type === IterateNoDocuments
}

export function isIteratorOverAllDocuments(iterator: DocumentMatchIteratorWeight): boolean {
  return iterator.type === IterateAllDocuments || iterator.cost === Number.MAX_SAFE_INTEGER
}

export function isIteratorUsingIndexOfDocuments(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorUsingIndex {
  return (iterator.type === IterateOverIndex)
}

export function *enumerateAllDocumentsInIndexIterator(iterator: DocumentMatchIteratorUsingIndex) {
  if (iterator.type === IterateOverIndex) {
    
    for (const documentId of iterator.set.values()) {
      yield documentId
    }
  } else if (isIteratorUsingJoinsOfIterators(iterator)) {
    for (const subIterator of iterator.iterators) {
      yield *enumerateAllDocumentsInIndexIterator(subIterator as DocumentMatchIteratorUsingIndex)
    }
  } else {
    throw new Error('Not supported')
  }
}

export function iteratorContains(iterator: DocumentMatchIteratorUsingIndex, documentId: Types.DocumentId): boolean {
  return iterator.set.has(documentId)
}

export function isIteratorUsingJoinsOfIterators(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorJoined {
  return iterator.type === IterateOverSubIterators
}

export function isIteratorUsingId(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorUsingId {
  return iterator.type === IterateOverId && 'id' in iterator
}

export function isIteratorOverNoDocuments(iterator: DocumentMatchIteratorWeight): iterator is DocumentMatchIteratorOverNoDocuments {
  return iterator.type === IterateNoDocuments || iterator.cost === 0
}

export interface OptimizationOptions {
  /**
   * Enable cost-based optimization for query intersection processing.
   * 
   * ## What it does:
   * When enabled, the query engine can remove high-cost iterators from intersection (AND) 
   * queries when a highly selective iterator is present. This reduces the computational 
   * cost of k-way merge operations by eliminating expensive document enumeration.
   * 
   * ## Why it's safe:
   * Iterator removal is semantically safe because:
   * 1. **Post-filtering ensures correctness**: The removed conditions are still enforced 
   *    by match.ts during the final document filtering phase
   * 2. **Superset enumeration**: The remaining iterator(s) return a superset of documents
   *    that potentially match the query, then post-filtering removes false positives
   * 3. **Performance vs correctness trade-off**: We exchange some enumeration overhead 
   *    for significantly reduced k-merge complexity
   * 
   * ## Optimization applies to:
   * - ✅ Intersection queries (AND): Safe to remove expensive iterators
   * - ❌ Union queries (OR): Never optimized - would change query semantics
   * 
   * @default false
   */
  enableCostBasedShortcut?: boolean
  
  /**
   * Fixed threshold for identifying highly selective iterators.
   * 
   * ## What it does:
   * If an iterator's cost (estimated number of documents) is below this threshold,
   * it's considered "highly selective" and triggers optimization. Other iterators
   * in the intersection may be removed to reduce k-merge overhead.
   * 
   * ## When to use:
   * - Set higher (e.g., 5000) for aggressive optimization in large datasets
   * - Set lower (e.g., 100) for conservative optimization 
   * - Use when you have predictable selective queries (e.g., ID lookups, status filters)
   * 
   * ## Safety:
   * This trigger is conservative - only activates when there's a genuinely selective
   * iterator that can efficiently enumerate a small candidate set. Post-filtering
   * via match.ts ensures all original conditions are still enforced.
   * 
   * @default 100
   */
  selectivityThreshold?: number
  
  /**
   * Cost ratio threshold for pairwise iterator optimization decisions.
   * 
   * ## What it does:
   * When comparing consecutive iterators (sorted by cost), removes an iterator 
   * and all subsequent ones if the cost ratio exceeds this threshold.
   * 
   * ## How it works:
   * - Higher multiplier (e.g., 100) = more aggressive optimization
   * - Lower multiplier (e.g., 2) = very conservative optimization  
   * - Formula: `nextIteratorCost / currentIteratorCost > costThresholdMultiplier`
   * 
   * ## Use cases:
   * - High multiplier (100): Keep iterators even if they're much more expensive
   * - Low multiplier (2): Remove iterators that are just 2x more expensive
   * - Medium multiplier (10): Balanced approach, removes very expensive iterators
   * 
   * ## Example:
   * Iterator costs [100, 500, 5000] with multiplier 10:
   * - First comparison: 500/100 = 5 ≤ 10 → keep both iterators
   * - Second comparison: 5000/500 = 10 ≤ 10 → keep all iterators  
   * - If multiplier was 5: 5000/500 = 10 > 5 → remove the 5000-cost iterator
   * 
   * ## Safety:
   * This optimization is safe for intersection queries because post-filtering
   * ensures correctness. Higher multipliers are safer but may reduce optimization.
   * 
   * @default 10 (remove iterators 10x more expensive than the previous one)
   */
  costThresholdMultiplier?: number
}

// Legacy type alias for backward compatibility
export type EnumerationOptions = OptimizationOptions

export function* enumerateGraphIterator(iterator: DocumentMatchIterator, allDocuments: Map<string, Types.Document>, stats?: SingleQueryStats): IterableIterator<Types.Document> {
  const documentsAlreadyReturned = new Set<string>()

  const info = {
    enumerated: 0,
    duplicates: 0,
  }

  stats?.phase('enumerate-graph', { iterator: describeIterator(iterator), info })

  const ops = stats?.newOpStats('enumerate-graph:yield')

  for (const document of _enumerateGraphIterator(iterator, allDocuments, stats)) {
    info.enumerated++
    const id = document.id

    ops?.beginOp()

    if (!documentsAlreadyReturned.has(id)) {
      documentsAlreadyReturned.add(id)
      yield document
    } else {
      info.duplicates++
    }

    ops?.endOp()
  }
}

function* _enumerateGraphIterator(
  iterator: DocumentMatchIterator,
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  if (isIteratorOverNoDocuments(iterator)) {
    return;
  } else if (isIteratorUsingId(iterator)) {
    const document = allDocuments.get(iterator.id);
    if (document) yield document;
  } else if (isIteratorUsingIndexOfDocuments(iterator)) {
    for (const documentId of iterator.set.keys()) {
      const document = allDocuments.get(documentId);
      if (document) yield document;
    }
  } else if (iterator.type === KMergedUnion) {
    // Use the efficient k-merge union implementation
    for (const documentId of enumerateKMergedUnion(iterator as KMergedUnionIterator, allDocuments, stats)) {
      const document = allDocuments.get(documentId);
      if (document) {
        stats?.increment('enumerated:union:k-merge');
        yield document;
      }
    }
  } else if (iterator.type === KMergedIntersection) {
    // Use the efficient k-merge intersection implementation
    for (const documentId of enumerateKMergedIntersection(iterator as KMergedIntersectionIterator, allDocuments, stats)) {
      const document = allDocuments.get(documentId);
      if (document) {
        stats?.increment('enumerated:intersection:k-merge');
        yield document;
      }
    }
  } else if (isIteratorUsingJoinsOfIterators(iterator)) {
    if (iterator.iterators.length === 1) {
      yield* _enumerateGraphIterator(iterator.iterators[0], allDocuments, stats);
    } else if (iterator.operator === 'intersect' && iterator.isSortable) {
      yield* _enumerateSortedIntersection(iterator.iterators, allDocuments, stats);
    } else if (iterator.operator === 'union' && iterator.isSortable) {
      yield* _enumerateSortedUnion(iterator.iterators, allDocuments, stats);
    } else if (iterator.operator === 'intersect') {
      yield* enumerateIntersectionOfIterators(iterator.iterators, allDocuments, stats);
    } else if (iterator.operator === 'union') {
      yield* enumerateUnionOfIterators(iterator.iterators, allDocuments, stats);
    }
  } else if (isIteratorOverAllDocuments(iterator)) {
    yield* allDocuments.values();
  } else {
    throw new Error('Unsupported iterator');
  }
}


export function buildIteratorForGraph(
  node: MatchExpression | Condition,
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats,
  options?: OptimizationOptions,
  isTopLevelCall = true
): DocumentMatchIterator {
  let iterator: DocumentMatchIterator;
  
  if (isFieldMatchNode(node)) {
    iterator = buildIteratorForFieldMatch(node, parameters, indexes, stats);
  } else if ('conditions' in node && node.conditions.length === 0) {
    stats?.mark('empty-conditions');
    iterator = { cost: 0, type: IterateNoDocuments, isSortable: true };
  } else if ('conditions' in node && node.conditions.length === 1) {
    iterator = buildIteratorForGraph(node.conditions[0], parameters, indexes, stats, options, false);
  } else if ('operator' in node) {
    iterator = buildIteratorForJoinedCondition(node as MatchExpressionMultiMatch, parameters, indexes, stats, options);
  } else {
    throw new Error(`Not supported ${JSON.stringify(node)}`);
  }

  if (isTopLevelCall) {
    stats?.phase('final-query-structure', { plan: describeIterator(iterator) });
  }

  return iterator;
}

function buildIteratorForFieldMatch(condition: FieldMatchNode, parameters: QueryParameters, indexes: IndexMap, stats?: SingleQueryStats): DocumentMatchIterator {
  // Handle NOT queries (termPrefix: "-")
  if (condition.termPrefix === '-') {
    return buildIteratorForNotFieldMatch(condition, parameters, indexes, stats)
  }

  if (condition.field === 'id') {
    const key = parameters[condition.term.id]

    return {
      type: IterateOverId,
      cost: 1,
      id: key,
      isSortable: true,
    }
  }

  const matchingIndex = indexes[condition.field]

  if (matchingIndex) {
    if ('term_min' in condition || 'term_max' in condition) {
      let keyMin = parameters[condition.term_min?.id]
      let keyMax = parameters[condition.term_max?.id]

      if (Array.isArray(keyMin) || Array.isArray(keyMax)) {
        throw new Error("Not implemented")
      }

      stats?.mark('range-iterator', { keyMin, keyMax })
 
      return buildIteratorForFieldRange(keyMin, keyMax, matchingIndex, stats)
    } else if ('term' in condition) { 
      const key = parameters[condition.term.id]

      if (Array.isArray(key)) {
        let   cost = 0
        const subIterators = []

        stats?.mark('multi-match')

        for (const value of key) {
          const subIterator = buildIteratorForFieldValue(value, matchingIndex, stats)
          cost += subIterator.cost
          subIterators.push(subIterator)
        }

        // For multi-value fields, this is a union operation - do not apply intersection optimizations
        const optimizedSubIterators = subIterators; // Keep all iterators for union
        const optimizedCost = cost;

        // Use K-merge union if all iterators are sortable
        if (optimizedSubIterators.every(it => it.isSortable)) {
          return {
            type: KMergedUnion,
            cost: optimizedCost,
            iterators: optimizedSubIterators,
            isSortable: true
          }
        } else {
          return {
            type: IterateOverSubIterators,
            operator: 'union',
            cost: optimizedCost,
            iterators: optimizedSubIterators as (DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined)[],
            isSortable: false
          }
        }
      } else {
        return buildIteratorForFieldValue(key, matchingIndex, stats)
      }
    }
  
    throw new Error('Not supported')
  } else {
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }
}

export function buildIteratorForFieldRange(fieldMin: any, fieldMax: any, index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  if (fieldMin === '*' && fieldMax === '*') {
    stats?.mark('all-documents-iterator')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if (typeof fieldMin === 'string') {
    if (fieldMin === '*') {
      fieldMin = '\x00'; // Represents the smallest possible string
    } else if (fieldMin.endsWith('*')) {
      fieldMin = fieldMin.slice(0, -1)
    }
  }
 
  if (typeof fieldMax === 'string') {
    if (fieldMax === '*') {
      fieldMax = '\uffff'; // Represents a very large string (for comparison)
    } else if (fieldMax.endsWith('*')) {
      fieldMax = fieldMax.slice(0, -1)
    }
  }

  const entriesWithKeys = index.getEntriesForRange(fieldMin, fieldMax);

  if (entriesWithKeys && entriesWithKeys.length > 0) {
    const subIterators: DocumentMatchIteratorUsingIndex[] = []; 

    for (const [key, setOfDocuments] of entriesWithKeys) { 
      if (setOfDocuments.size > 0) { 
        const numberOfDocuments = setOfDocuments.size;
        subIterators.push({ 
          type: IterateOverIndex, 
          index, 
          key: key as string, 
          cost: numberOfDocuments, 
          set: setOfDocuments,
          isSortable: index.isBTree
        });
      }
    }

    const subIteratorsMatched = subIterators.length
    
    if (subIterators.length === 0) { 
      stats?.mark('range-iterator:none', { fieldMin, fieldMax, cost: 0 })
      return { type: IterateNoDocuments, index, key: fieldMin ?? fieldMax, cost: 0, isSortable: true };
    }

    if (subIterators.length === 1) {
      stats?.mark('range-iterator:single', { fieldMin, fieldMax, cost: subIterators[0].cost})
      return subIterators[0];
    }

    const totalCost = subIterators.reduce((acc, iterator) => acc + iterator.cost, 0);
    stats?.mark('range-iterator:union', { fieldMin, fieldMax, subIteratorsMatched, cost: totalCost })

    // For range queries, this is a union operation - do not apply intersection optimizations
    const optimizedSubIterators = subIterators; // Keep all iterators for union
    const optimizedCost = totalCost;

    // Use K-merge union if all iterators are sortable
    if (optimizedSubIterators.every(it => it.isSortable)) {
      return {
        type: KMergedUnion,
        cost: optimizedCost,
        iterators: optimizedSubIterators,
        isSortable: true
      };
    } else {
      return {
        type: IterateOverSubIterators,
        operator: 'union', 
        cost: optimizedCost,
        iterators: optimizedSubIterators as (DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined)[],
        isSortable: false
      };
    }
  } else {
    return { type: IterateNoDocuments, index, key: fieldMin ?? fieldMax, cost: 0, isSortable: true };
  } 
}

export function buildIteratorForFieldValue(fieldValue: any, index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  if (typeof fieldValue === 'string' && fieldValue.startsWith('*')) {
    stats?.mark('all-documents-iterator')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if (typeof fieldValue === 'string' && fieldValue.endsWith('*')) {
    fieldValue = fieldValue.slice(0, -1)
  }

  const setOfDocuments = index.getDocumentIdsForValue(fieldValue)

  if (setOfDocuments) {
    const numberOfDocuments = setOfDocuments.size
    const it = { type: IterateOverIndex, index, key: fieldValue, cost: numberOfDocuments, set: setOfDocuments, isSortable: index.isBTree }
    stats?.mark('field-value-iterator', { cost: it.cost, iterator: describeIterator(it) })
    return it
  } else {
    stats?.mark('field-value-iterator:none')
    return { type: IterateNoDocuments, index, key: fieldValue, cost: 0, isSortable: true }
  } 
}

function buildIteratorForNotFieldMatch(condition: FieldMatchNode, parameters: QueryParameters, indexes: IndexMap, stats?: SingleQueryStats): DocumentMatchIterator {
  // For id field, NOT queries can't be optimized easily, fall back to all documents
  if (condition.field === 'id') {
    stats?.mark('not-query:id-field-all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  const matchingIndex = indexes[condition.field]

  if (!matchingIndex) {
    // No index available, must iterate all documents
    stats?.mark('not-query:no-index-all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if ('term_min' in condition || 'term_max' in condition) {
    // NOT range queries are complex, fall back to all documents for now
    stats?.mark('not-query:range-all-documents')
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false }
  }

  if ('term' in condition) {
    const excludeKey = parameters[condition.term.id]

    if (Array.isArray(excludeKey)) {
      // NOT with multiple values - enumerate all other values
      return buildIteratorForNotMultipleValues(excludeKey, matchingIndex, stats)
    } else {
      // NOT with single value - enumerate all other values
      return buildIteratorForNotSingleValue(excludeKey, matchingIndex, stats)
    }
  }

  throw new Error('NOT query not supported for this field type')
}

function buildIteratorForNotSingleValue(excludeValue: any, index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  stats?.mark('not-query:single-value-complement')
  
  // Get all values from the index except the excluded one
  const allValues = Array.from((index as any)._valuesToDocumentIds.keys())
  
  stats?.mark('not-query:single-value-complement', { allValuesCount: allValues.length, excludeValue })
  const complementValues = allValues.filter(value => value !== excludeValue)
  
  if (complementValues.length === 0) {
    // All documents have the excluded value, so NOT query matches nothing
    return { type: IterateNoDocuments, index, key: excludeValue, cost: 0, isSortable: true }
  }

  // Create iterators for all complement values
  const subIterators = complementValues.map(value => buildIteratorForFieldValue(value, index, stats))
  
  // For NOT queries, this is a union operation - do not apply intersection optimizations
  const optimizedSubIterators = subIterators; // Keep all iterators for union
  const optimizedCost = optimizedSubIterators.reduce((sum, it) => sum + it.cost, 0);

  // Use K-merge union if all iterators are sortable
  if (optimizedSubIterators.every(it => it.isSortable)) {
    return {
      type: KMergedUnion,
      cost: optimizedCost,
      iterators: optimizedSubIterators,
      isSortable: true
    }
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'union',
      cost: optimizedCost,
      iterators: optimizedSubIterators as (DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined)[],
      isSortable: false
    }
  }
}

function buildIteratorForNotMultipleValues(excludeValues: any[], index: Index, stats?: SingleQueryStats): DocumentMatchIterator {
  stats?.mark('not-query:multiple-values-complement')
  
  // Get all values from the index except the excluded ones
  const excludeSet = new Set(excludeValues)
  const allValues = Array.from((index as any)._valuesToDocumentIds.keys())
  const complementValues = allValues.filter(value => !excludeSet.has(value))
  
  if (complementValues.length === 0) {
    // All documents have one of the excluded values, so NOT query matches nothing
    return { type: IterateNoDocuments, index, key: excludeValues[0], cost: 0, isSortable: true }
  }

  // Create iterators for all complement values
  const subIterators = complementValues.map(value => buildIteratorForFieldValue(value, index, stats))
  
  // For NOT queries, this is a union operation - do not apply intersection optimizations
  const optimizedSubIterators = subIterators; // Keep all iterators for union
  const optimizedCost = optimizedSubIterators.reduce((sum, it) => sum + it.cost, 0);

  // Use K-merge union if all iterators are sortable
  if (optimizedSubIterators.every(it => it.isSortable)) {
    return {
      type: KMergedUnion,
      cost: optimizedCost,
      iterators: optimizedSubIterators,
      isSortable: true
    }
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'union',
      cost: optimizedCost,
      iterators: optimizedSubIterators as (DocumentMatchIteratorUsingId | DocumentMatchIteratorUsingIndex | DocumentMatchIteratorJoined)[],
      isSortable: false
    }
  }
}

export function buildIteratorForJoinedCondition(node: MatchExpressionMultiMatch, parameters: QueryParameters, indexes: IndexMap, stats?: SingleQueryStats, options?: OptimizationOptions): DocumentMatchIterator {
  if (node.operator === 'AND') {
    return buildIntersectionIterator(node.conditions, parameters, indexes, stats, options)
  } else if (node.operator === 'OR') {
    return buildUnionIterator(node.conditions, parameters, indexes, stats)
  } else {
    throw new Error('Unsupported iterator')
  }
}

export function buildIntersectionIterator(
  conditions: Condition[],
  parameters: QueryParameters,
  indexes: IndexMap,
  stats?: SingleQueryStats,
  options?: OptimizationOptions
): DocumentMatchIteratorJoined | DocumentMatchIteratorOverNoDocuments | DocumentMatchIteratorAllDocuments {
  const iteratorsToJoin = conditions.map(c => buildIteratorForGraph(c, parameters, indexes, stats, options, false));

  // Flatten nested intersections and collect all iterators
  const flattenedIterators: (DocumentMatchIteratorUsingId | DocumentMatchIteratorJoined | DocumentMatchIteratorUsingIndex)[] = [];
  let minimumCost = Number.MAX_SAFE_INTEGER;

  for (const iterator of iteratorsToJoin) {
    if (isIteratorOverNoDocuments(iterator)) {
      stats?.mark('intersection-iterator:none');
      return iterator;
    }
    
    if (isIteratorOverAllDocuments(iterator)) {
      // Skip "all documents" iterators in intersection - they don't constrain the result
      continue;
    }

    // Flatten nested intersections
    if (isIteratorUsingJoinsOfIterators(iterator) && iterator.operator === 'intersect') {
      flattenedIterators.push(...iterator.iterators);
      if (iterator.cost < minimumCost) {
        minimumCost = iterator.cost;
      }
    } else if (
      isIteratorUsingId(iterator) ||
      isIteratorUsingIndexOfDocuments(iterator) ||
      isIteratorUsingJoinsOfIterators(iterator) ||
      iterator.type === KMergedUnion ||
      iterator.type === KMergedIntersection
    ) {
      flattenedIterators.push(iterator as any);
      if (iterator.cost < minimumCost) {
        minimumCost = iterator.cost;
      }
    } else {
      console.warn('Unsupported iterator type for intersection:', iterator);
      stats?.mark('intersection-iterator:unsupported', { iterator: describeIterator(iterator) });
      continue;
    }
  }

  // Apply optimization at this level for intersection operations
  let optimizedIterators = flattenedIterators;
  if (options?.enableCostBasedShortcut && flattenedIterators.length > 1) {
    optimizedIterators = optimizeSiblingIterators(flattenedIterators, options, stats, 'intersect') as (DocumentMatchIteratorUsingId | DocumentMatchIteratorJoined | DocumentMatchIteratorUsingIndex)[];
    
    // Update minimum cost based on optimized iterators
    if (optimizedIterators.length !== flattenedIterators.length) {
      minimumCost = Math.min(...optimizedIterators.map(it => it.cost));
    }
  }

  if (optimizedIterators.length === 0) {
    stats?.mark('intersection-iterator:all-documents');
    return { type: IterateAllDocuments, cost: Number.MAX_SAFE_INTEGER, isSortable: false };
  }
  
  if (optimizedIterators.length === 1) {
    const singleIterator = optimizedIterators[0];
    stats?.mark('intersection-iterator:single', { iterator: describeIterator(singleIterator), cost: singleIterator.cost });
    return singleIterator;
  }

  // Sort iterators by cost to optimize enumeration (smallest first)
  optimizedIterators.sort((a, b) => a.cost - b.cost);

  // Check if we can use k-merge intersection (all iterators are sortable)
  const allSortable = optimizedIterators.every((it: any) => it.isSortable);
  if (allSortable && optimizedIterators.length > 2) {
    stats?.mark('intersection-iterator:k-merge', {
      cost: minimumCost,
      iteratorCount: optimizedIterators.length,
      iterators: optimizedIterators.map((i: any) => ({
        type: i.type.description,
        cost: i.cost
      }))
    });

    return {
      type: KMergedIntersection,
      cost: minimumCost,
      iterators: optimizedIterators,
      isSortable: true
    };
  }

  // Fall back to traditional nested intersection
  stats?.mark('intersection-iterator:traditional', {
    cost: minimumCost,
    iteratorCount: optimizedIterators.length,
    iterators: optimizedIterators.map((i: any) => ({
      type: i.type.description,
      cost: i.cost,
      description: describeIterator(i)
    }))
  });

  // Use K-merge intersection if all iterators are sortable
  if (allSortable) {
    return {
      type: KMergedIntersection,
      cost: minimumCost,
      iterators: optimizedIterators,
      isSortable: true
    };
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'intersect',
      cost: minimumCost,
      iterators: optimizedIterators,
      isSortable: false
    };
  }
}

export function buildUnionIterator(conditions: Condition[], parameters: QueryParameters, indexes: IndexMap, stats?: SingleQueryStats): DocumentMatchIteratorJoined|DocumentMatchIteratorAllDocuments {
  const iteratorsToJoin = conditions.map(c => buildIteratorForGraph(c, parameters, indexes))

  // Flatten nested unions and collect all iterators
  const flattenedIterators: (DocumentMatchIteratorJoined|DocumentMatchIteratorUsingIndex|DocumentMatchIteratorUsingId)[] = []
  let totalCost = 0

  for (const iterator of iteratorsToJoin) {
    if (isIteratorOverAllDocuments(iterator)) {
      stats?.mark('union-iterator:all-documents', { cost: Number.MAX_SAFE_INTEGER })
      return iterator
    }
    
    if (isIteratorOverNoDocuments(iterator)) {
      // Skip empty iterators in union
      continue
    }
    
    // Flatten nested unions
    if (isIteratorUsingJoinsOfIterators(iterator) && iterator.operator === 'union') {
      flattenedIterators.push(...iterator.iterators)
      totalCost += iterator.cost
    } else {
      flattenedIterators.push(iterator as any)
      totalCost += (iterator as any).cost
    }
  }

  if (flattenedIterators.length === 0) {
    stats?.mark('union-iterator:none')
    return { type: IterateNoDocuments, cost: 0, isSortable: true }
  }
  
  if (flattenedIterators.length === 1) {
    const singleIterator = flattenedIterators[0]
    stats?.mark('union-iterator:single', { cost: totalCost, iterator: describeIterator(singleIterator) })
    return singleIterator
  }

  // Check if we can use k-merge union (all iterators are sortable)
  const allSortable = flattenedIterators.every(it => it.isSortable)
  if (allSortable && flattenedIterators.length > 2) {
    stats?.mark('union-iterator:k-merge', { 
      cost: totalCost, 
      iteratorCount: flattenedIterators.length,
      iterators: flattenedIterators.map(i => ({ type: i.type.description, cost: i.cost }))
    })
    
    return {
      type: KMergedUnion,
      cost: totalCost,
      iterators: flattenedIterators,
      isSortable: true
    }
  }

  // Fall back to traditional nested union
  stats?.mark('union-iterator:traditional', { 
    cost: totalCost, 
    iteratorCount: flattenedIterators.length,
    iterators: flattenedIterators.map(i => ({ type: i.type.description, cost: i.cost, description: describeIterator(i) }))
  })

  // Use K-merge union if all iterators are sortable
  if (allSortable) {
    return {
      type: KMergedUnion,
      cost: totalCost,
      iterators: flattenedIterators,
      isSortable: true
    }
  } else {
    return {
      type: IterateOverSubIterators,
      operator: 'union',
      cost: totalCost,
      iterators: flattenedIterators,
      isSortable: false
    }
  }
}

function* _enumerateSortedUnion(
  iterators: DocumentMatchIterator[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  // Initialize cursors
  type Cursor = { iter: IterableIterator<Types.Document>; value: Types.Document | undefined };
  const cursors: Cursor[] = iterators
    .map(it => {
      const iter = _enumerateGraphIterator(it, allDocuments, stats);
      const next = iter.next();
      return { iter, value: next.done ? undefined : next.value };
    })
    .filter(c => c.value);

  if (cursors.length === 0) return;

  while (cursors.length > 0) {
    // Find minimum ID
    let vmin = cursors[0].value!.id;
    for (const c of cursors) {
      if (c.value!.id < vmin) vmin = c.value!.id;
    }

    // Yield the document
    stats?.increment('enumerated:union:sorted')
    yield cursors.find(c => c.value!.id === vmin)!.value!;

    // Advance cursors at vmin
    for (let i = 0; i < cursors.length; ) {
      if (cursors[i].value!.id === vmin) {
        const next = cursors[i].iter.next();
        cursors[i].value = next.done ? undefined : next.value;
        if (!cursors[i].value) cursors.splice(i, 1);
        else i++;
      } else {
        i++;
      }
    }
  }
}

export function* enumerateIntersectionOfIterators(iterators: DocumentMatchIterator[], allDocuments: Map<string, Types.Document>, stats: SingleQueryStats): IterableIterator<Types.Document> {
  if (iterators.length <= 1) {
    throw new Error('Unsupported number of iterators to intersect')
  }

  if (iterators.some(it => isIteratorOverNoDocuments(it))) {
    stats?.mark('intersection-iterator:none')
    return
  }

  // Check if we can optimize the intersection
  if (iterators.length > 1 && iterators.every(it => isIteratorUsingIndexOfDocuments(it) && it.index.isBTree )) {

    yield* _enumerateBTreeIntersection(
      iterators as DocumentMatchIteratorUsingIndex[],
      allDocuments,
      stats
    );
    return;
  }


  const outerIterator = iterators[0]
  const innerIterators = iterators.slice(1)

  for (const document of _enumerateGraphIterator(outerIterator, allDocuments, stats)) {
    let containedInAllOtherIterators = true

    for (const innerIterator of innerIterators) {
      if (isIteratorOverAllDocuments(innerIterator)) {
        continue
      }

      if (isIteratorUsingIndexOfDocuments(innerIterator) ) {
        if (!iteratorContains(innerIterator, document.id)) {
          // We are handling an intersection so if this documentis not in this iterator 
          // it means it is not in the intersection (and we do not need to check the other iterators)
          containedInAllOtherIterators = false
          break
        }
      }

      if (isIteratorUsingJoinsOfIterators(innerIterator)) {
        let foundInIterator = false

        for (const innerDocument of _enumerateGraphIterator(innerIterator, allDocuments, stats)) {
          if (document.id === innerDocument.id) {
            foundInIterator = true
            break
          }
        }

        if (!foundInIterator) {
          containedInAllOtherIterators = false
          break
        }
      }
    }

    if (containedInAllOtherIterators) {
      stats?.increment('enumerated:intersection:set')
      yield document
    }
  }
}

export function* enumerateUnionOfIterators(iterators: (DocumentMatchIteratorJoined|DocumentMatchIteratorUsingIndex|DocumentMatchIteratorUsingId)[], allDocuments: Map<string, Types.Document>, stats?: SingleQueryStats): IterableIterator<Types.Document> {
  if (iterators.length > 1 && iterators.every(it => isIteratorUsingIndexOfDocuments(it) && it.index.isBTree)) {
    yield* _enumerateBTreeUnion(
      iterators as DocumentMatchIteratorUsingIndex[],
      allDocuments,
      stats
    )
    return
  }


  for (const iterator of iterators) {
    for (const doc of _enumerateGraphIterator(iterator, allDocuments, stats)) {
      stats?.increment('enumerated:union:set')
      yield doc
    }
  }
}

/**
 * Intersect a list of B-tree index iterators and enumerate the common documents.
 *
 * Preconditions
 *  • iterators.length ≥ 2
 *  • every iterator is { type: IterateOverIndex, set: BTree<string>, … }
 *  • index.isBTree === true  (so set supports .minKey(), .maxKey() and .entries(from, holder))
 */
export function* _enumerateBTreeIntersection (
  iterators : DocumentMatchIteratorUsingIndex[],
  allDocs   : Map<string, Types.Document>,
  stats?    : SingleQueryStats
): IterableIterator<Types.Document> {

  /* ------------------------------------------------------------------ *
   * 1.  Early “no-overlap” check using   [globalLo .. globalHi]         *
   * ------------------------------------------------------------------ */
  let globalLo = '';
  let globalHi = '\uffff';

  for (const it of iterators) {
    const bt = it.set as BTreeModule<string>;                                    // BTree<string>
    if (bt.size === 0) return;                            // empty ⇒ empty
    const lo = bt.minKey() as string;                     // 1st key
    const hi = bt.maxKey() as string;                     // last key
    if (lo > globalLo) globalLo = lo;
    if (hi < globalHi) globalHi = hi;
  }
  
  // ranges don’t overlap
  if (globalLo > globalHi) {
    stats?.mark('btree-intersect:empty');
    return;                        
  }

  /* ------------------------------------------------------------------ *
   * 2.  Cursor initialisation                                           *
   * ------------------------------------------------------------------ */
  type Cursor = {
    bt    : BTreeModule<string>,          // the set
    iter  : Iterator<string>,             // .entries() gives [k,v] but .keys() is simpler
    value : string | undefined;           // current doc-id
  };

  const cursors: Cursor[] = iterators.map(it => {
    const bt = it.set as BTreeModule<string>;

    /* `keys(from)`     –  sorted-btree v5+ helper that jumps to ≥ from            *
     * `entries(from,x)` –  older API, holder[0] is the key                        */
    let v: string | undefined;
    let iter: Iterator<string>;

    if (typeof (bt as any).keys === 'function') {
      iter  = (bt as any).keys(globalLo)[Symbol.iterator]();
      v     = (iter.next().value as string|undefined);
    } else {
      const holder: [string] = [undefined as unknown as string];
      iter  = (bt as any).entries(globalLo, holder);
      v     = iter.next().done ? undefined : holder[0];
    }

    return { bt, iter, value: v };
  });

  if (cursors.some(c => c.value === undefined)) return;   // one set exhausted

  const bTreeIntersectInfo = { k: cursors.length, loop: 0, globalLo, globalHi }
  stats?.mark('btree-intersect:start', bTreeIntersectInfo);

  /* ------------------------------------------------------------------ *
   * 3.  Multi-way merge/skip loop                                       *
   * ------------------------------------------------------------------ */
  while (true) {
    bTreeIntersectInfo.loop ++

    // current maximum value among the cursors
    let vmax = cursors[0].value as string;
    for (let i = 1; i < cursors.length; ++i) {
      const v = cursors[i].value as string;
      if (v > vmax) vmax = v;
    }

    // advance every cursor whose value < vmax up to ≥ vmax
    for (const c of cursors) {
      while (c.value !== undefined && c.value < vmax) {
        const n = c.iter.next();
        c.value = n.done ? undefined : (n.value ?? (n as any).value); // handle entries(holder)
      }
      if (c.value === undefined) return;             // one exhausted ⇒ done
      if (c.value > vmax) vmax = c.value;            // vmax may grow → restart outer loop
    }

    // if they all line up we found a common document-id
    const aligned = cursors.every(c => c.value === vmax);
    if (aligned) {
      const doc = allDocs.get(vmax);
      if (doc) {
        stats?.increment('enumerated:intersection:btree')
        yield doc;
      }
      // step all cursors once and continue
      for (const c of cursors) {
        const n = c.iter.next();
        c.value = n.done ? undefined : (n.value ?? (n as any).value);
        if (c.value === undefined) return;           // exhausted
      }
    }
    /* else: vmax changed, outer while() repeats */
  }
}

function* _enumerateSortedIntersection(
  iterators: DocumentMatchIterator[],
  allDocuments: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  if (iterators.length < 2) return;
  if (iterators.some(it => isIteratorOverNoDocuments(it))) return;

  // Initialize cursors for each iterator
  type Cursor = { iter: IterableIterator<Types.Document>; value: Types.Document | undefined };
  const cursors: Cursor[] = iterators.map(it => {
    const iter = _enumerateGraphIterator(it, allDocuments, stats);
    const next = iter.next();
    return { iter, value: next.done ? undefined : next.value };
  });

  if (cursors.some(c => !c.value)) return;

  while (true) {
    // Find maximum ID among cursors
    let vmax = cursors[0].value!.id;
    for (const c of cursors) {
      if (c.value!.id > vmax) vmax = c.value!.id;
    }

    // Advance cursors < vmax
    for (const c of cursors) {
      while (c.value && c.value.id < vmax) {
        const next = c.iter.next();
        c.value = next.done ? undefined : next.value;
      }
      if (!c.value) return;
    }

    // Check if all cursors align
    if (cursors.every(c => c.value!.id === vmax)) {
      stats?.increment('enumerated:intersection:sorted')
      yield cursors[0].value!;
      for (const c of cursors) {
        const next = c.iter.next();
        c.value = next.done ? undefined : next.value;
      }
      if (cursors.some(c => !c.value)) return;
    }
  }
}

/**
 * Enumerate the union of documents from a list of B-tree index iterators.
 *
 * Preconditions
 *  • iterators.length ≥ 1
 *  • every iterator is { type: IterateOverIndex, set: BTree<string>, … }
 *  • index.isBTree === true  (so set supports .minKey(), .maxKey() and .entries(from, holder))
 */
export function* _enumerateBTreeUnion(
  iterators: DocumentMatchIteratorUsingIndex[],
  allDocs: Map<string, Types.Document>,
  stats?: SingleQueryStats
): IterableIterator<Types.Document> {
  /* ------------------------------------------------------------------ *
   * 1. Handle edge cases                                               *
   * ------------------------------------------------------------------ */
  if (iterators.length === 0) return;

  /* ------------------------------------------------------------------ *
   * 2. Cursor initialization                                           *
   * ------------------------------------------------------------------ */
  type Cursor = {
    bt: BTreeModule<string>, // the set
    iter: Iterator<string>, // .entries() gives [k,v] but .keys() is simpler
    value: string | undefined // current doc-id
  }

  const cursors: Cursor[] = iterators.map((it) => {
    const bt = it.set as BTreeModule<string>

    let v: string | undefined
    let iter: Iterator<string>

    if (typeof (bt as any).keys === 'function') {
      iter = (bt as any).keys()[Symbol.iterator]()
      v = iter.next().value as string | undefined
    } else {
      const holder: [string] = [undefined as unknown as string]
      iter = (bt as any).entries(undefined, holder)
      v = iter.next().done ? undefined : holder[0]
    }

    return { bt, iter, value: v }
  })

  // Filter out exhausted cursors (empty sets)
  const activeCursors = cursors.filter((c) => c.value !== undefined)
  if (activeCursors.length === 0) return

  const bTreeUnionInfo = { k: cursors.length, loop: 0 }
  stats?.mark('btree-union:start', bTreeUnionInfo)

  /* ------------------------------------------------------------------ *
   * 3. Multi-way merge loop                                            *
   * ------------------------------------------------------------------ */
  while (activeCursors.length > 0) {
    bTreeUnionInfo.loop++
    
    // Find the minimum value among active cursors
    let vmin = activeCursors[0].value as string
    for (let i = 1; i < activeCursors.length; i++) {
      const v = activeCursors[i].value as string
      if (v < vmin) vmin = v
    }

    // Yield the document for vmin if it exists
    const doc = allDocs.get(vmin)
    if (doc) {
      stats?.increment('enumerated:union:btree')
      yield doc
    }

    // Advance all cursors that are at vmin
    for (let i = 0; i < activeCursors.length; ) {
      const c = activeCursors[i]
      if (c.value === vmin) {
        const n = c.iter.next()
        c.value = n.done ? undefined : n.value ?? (n as any).value
        if (c.value === undefined) {
          // Remove exhausted cursor
          activeCursors.splice(i, 1)
        } else {
          i++
        }
      } else {
        i++
      }
    }
  }
}

